import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, Alert,
  Paper, CircularProgress
} from '@mui/material';
import equipmentService from '../../services/equipmentService';
import { useToast } from '../ToastProvider';

export default function EquipmentRequestForm({ onSuccess }) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const [form, setForm] = useState({
    categoryId: '',
    subcategoryId: '',
    quantity: 1,
    justification: '',
    preferredBrand: '',
    preferredModel: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (form.categoryId) {
      loadSubcategories(form.categoryId);
    } else {
      setSubcategories([]);
    }
  }, [form.categoryId]);

  const loadCategories = async () => {
    try {
      const data = await equipmentService.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryId) => {
    try {
      const data = await equipmentService.getSubcategories(categoryId);
      setSubcategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load subcategories');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'categoryId' ? { subcategoryId: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subcategoryId) {
      setError('Please select an item type');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await equipmentService.createRequest({
        subcategoryId: parseInt(form.subcategoryId),
        quantity: parseInt(form.quantity),
        justification: form.justification || null,
        preferredBrand: form.preferredBrand || null,
        preferredModel: form.preferredModel || null
      });
      toast.success('Equipment request submitted successfully!');
      setForm({
        categoryId: '',
        subcategoryId: '',
        quantity: 1,
        justification: '',
        preferredBrand: '',
        preferredModel: ''
      });
      if (onSuccess) onSuccess();
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSubcategory = subcategories.find(s => s.id === parseInt(form.subcategoryId));

  if (loading) return <CircularProgress />;

  return (
    <Paper sx={{ p: 3, maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        Request New Equipment
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            fullWidth
            label="Category"
            name="categoryId"
            value={form.categoryId}
            onChange={handleChange}
            required
          >
            {categories.length === 0 && (
              <MenuItem value="" disabled>No categories available</MenuItem>
            )}
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Item Type"
            name="subcategoryId"
            value={form.subcategoryId}
            onChange={handleChange}
            required
            disabled={!form.categoryId}
            helperText={form.categoryId && subcategories.length === 0 ? 'No item types for this category. Contact admin.' : ''}
          >
            {subcategories.length === 0 && (
              <MenuItem value="" disabled>
                {form.categoryId ? 'No item types available' : 'Select a category first'}
              </MenuItem>
            )}
            {subcategories.map(sub => (
              <MenuItem key={sub.id} value={sub.id}>
                {sub.name} {sub.estimatedPrice ? `(~${sub.estimatedPrice.toFixed(0)} EUR)` : ''}
              </MenuItem>
            ))}
          </TextField>

          {selectedSubcategory?.estimatedPrice && (
            <Alert severity="info">
              Estimated price: {selectedSubcategory.estimatedPrice.toFixed(2)} EUR
            </Alert>
          )}

          <TextField
            fullWidth
            type="number"
            label="Quantity"
            name="quantity"
            value={form.quantity}
            onChange={handleChange}
            inputProps={{ min: 1, max: 10 }}
            required
            sx={{ maxWidth: 150 }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Justification"
            name="justification"
            value={form.justification}
            onChange={handleChange}
            placeholder="Why do you need this equipment?"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Preferred Brand (optional)"
              name="preferredBrand"
              value={form.preferredBrand}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label="Preferred Model (optional)"
              name="preferredModel"
              value={form.preferredModel}
              onChange={handleChange}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ mt: 1, alignSelf: 'flex-start' }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
}

